/**
 * Phone number utilities for phone-based authentication
 * Supports North America: US/CA (+1), Mexico (+52)
 */

/** Country codes supported for phone auth */
export const SUPPORTED_COUNTRIES = [
  { code: '+1', name: 'US/Canada', flag: 'US', maxDigits: 10 },
  { code: '+52', name: 'Mexico', flag: 'MX', maxDigits: 10 },
] as const;

export type CountryCode = typeof SUPPORTED_COUNTRIES[number]['code'];

/** Result of phone validation */
export interface PhoneValidationResult {
  isValid: boolean;
  normalized: string | null;
  error?: string;
}

/**
 * Validates and normalizes a phone number to E.164 format
 * @param phone - The phone number to validate (can include formatting)
 * @param countryCode - The country code (e.g., '+1', '+52')
 * @returns Validation result with normalized phone in E.164 format
 */
export function validateAndNormalizePhone(
  phone: string,
  countryCode: CountryCode = '+1'
): PhoneValidationResult {
  if (!phone || phone.trim() === '') {
    return { isValid: false, normalized: null, error: 'Phone number is required' };
  }

  // Extract only digits from the input
  const digits = phone.replace(/\D/g, '');

  // Get the expected length for this country
  const country = SUPPORTED_COUNTRIES.find(c => c.code === countryCode);
  if (!country) {
    return { isValid: false, normalized: null, error: 'Unsupported country code' };
  }

  // Handle different input formats
  let normalizedDigits = digits;

  // If user entered with country code already
  if (countryCode === '+1') {
    if (digits.length === 11 && digits.startsWith('1')) {
      normalizedDigits = digits.substring(1);
    } else if (digits.length !== 10) {
      return {
        isValid: false,
        normalized: null,
        error: 'US/Canada phone numbers must be 10 digits',
      };
    }
  } else if (countryCode === '+52') {
    // Mexico numbers: 10 digits, or 12 starting with 52, or 13 starting with 521
    if (digits.length === 10) {
      normalizedDigits = digits;
    } else if (digits.length === 12 && digits.startsWith('52')) {
      normalizedDigits = digits.substring(2);
    } else if (digits.length === 13 && digits.startsWith('521')) {
      normalizedDigits = digits.substring(3);
    } else {
      return {
        isValid: false,
        normalized: null,
        error: 'Mexico phone numbers must be 10 digits',
      };
    }
  }

  // Validate the normalized digits
  if (normalizedDigits.length !== country.maxDigits) {
    return {
      isValid: false,
      normalized: null,
      error: `Phone number must be ${country.maxDigits} digits`,
    };
  }

  // Build E.164 format
  const normalized = `${countryCode}${normalizedDigits}`;

  return { isValid: true, normalized };
}

/**
 * Converts a phone number to the pseudo-email format used for Supabase Auth
 * @param phone - Phone number (can be raw or E.164 format)
 * @param countryCode - Country code if phone is raw
 * @returns Pseudo-email like +15551234567@phone.local, or null if invalid
 */
export function phoneToAuthEmail(
  phone: string,
  countryCode: CountryCode = '+1'
): string | null {
  // If already in E.164 format (starts with +)
  if (phone.startsWith('+')) {
    const digits = phone.replace(/\D/g, '');
    if (digits.length >= 10 && digits.length <= 13) {
      return `+${digits}@phone.local`;
    }
    return null;
  }

  // Validate and normalize
  const result = validateAndNormalizePhone(phone, countryCode);
  if (!result.isValid || !result.normalized) {
    return null;
  }

  return `${result.normalized}@phone.local`;
}

/**
 * Extracts the phone number from a pseudo-email
 * @param email - The pseudo-email (e.g., +15551234567@phone.local)
 * @returns The phone number in E.164 format, or null if not a phone email
 */
export function authEmailToPhone(email: string): string | null {
  if (!email || !email.endsWith('@phone.local')) {
    return null;
  }
  return email.replace('@phone.local', '');
}

/**
 * Checks if an email is a phone-based pseudo-email
 * @param email - The email to check
 * @returns true if this is a phone auth email
 */
export function isPhoneAuthEmail(email: string | null | undefined): boolean {
  return !!email && email.endsWith('@phone.local');
}

/**
 * Formats a phone number for display
 * @param phone - Phone in E.164 format (e.g., +15551234567)
 * @returns Formatted string (e.g., (555) 123-4567)
 */
export function formatPhoneDisplay(phone: string | null | undefined): string {
  if (!phone) return '';

  // Extract digits
  const digits = phone.replace(/\D/g, '');

  // Handle US/Canada (+1)
  if (digits.length === 11 && digits.startsWith('1')) {
    const areaCode = digits.substring(1, 4);
    const prefix = digits.substring(4, 7);
    const line = digits.substring(7, 11);
    return `(${areaCode}) ${prefix}-${line}`;
  }

  // Handle 10 digits (no country code)
  if (digits.length === 10) {
    const areaCode = digits.substring(0, 3);
    const prefix = digits.substring(3, 6);
    const line = digits.substring(6, 10);
    return `(${areaCode}) ${prefix}-${line}`;
  }

  // Handle Mexico (+52)
  if (digits.length === 12 && digits.startsWith('52')) {
    const part1 = digits.substring(2, 5);
    const part2 = digits.substring(5, 8);
    const part3 = digits.substring(8, 12);
    return `+52 ${part1} ${part2} ${part3}`;
  }

  // Return as-is if we can't format it
  return phone;
}

/**
 * Formats a phone number as the user types (for input masking)
 * @param value - Current input value
 * @param countryCode - Country code for formatting rules
 * @returns Formatted phone number
 */
export function formatPhoneInput(value: string, countryCode: CountryCode = '+1'): string {
  // Extract only digits
  const digits = value.replace(/\D/g, '');

  if (countryCode === '+1') {
    // US/Canada format: (XXX) XXX-XXXX
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }

  if (countryCode === '+52') {
    // Mexico format: XXX XXX XXXX
    if (digits.length === 0) return '';
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
  }

  return digits;
}

/**
 * Gets the raw digits from a formatted phone input
 * @param formattedPhone - The formatted phone string
 * @returns Just the digits
 */
export function getPhoneDigits(formattedPhone: string): string {
  return formattedPhone.replace(/\D/g, '');
}

/** Domain used for guest pseudo-emails */
export const GUEST_EMAIL_DOMAIN = '@guest.local';

/**
 * Generates a pseudo-email for guest bookings using their phone number
 * Format: +1234567890@guest.local (same pattern as phone auth)
 * @param phone - Phone number (can be raw or E.164 format)
 * @returns Pseudo-email for guest bookings
 */
export function generateGuestPseudoEmail(phone: string): string {
  // Normalize phone to E.164 format if not already
  const normalizedPhone = phone.startsWith('+') ? phone : `+1${phone.replace(/\D/g, '')}`;
  return `${normalizedPhone}${GUEST_EMAIL_DOMAIN}`;
}

/**
 * Checks if an email is a guest pseudo-email
 * @param email - The email to check
 * @returns true if this is a guest pseudo-email
 */
export function isGuestPseudoEmail(email: string | null | undefined): boolean {
  return !!email && email.endsWith(GUEST_EMAIL_DOMAIN);
}

/**
 * Checks if an email is any kind of pseudo-email (phone auth or guest)
 * @param email - The email to check
 * @returns true if this is a pseudo-email that shouldn't receive real emails
 */
export function isPseudoEmail(email: string | null | undefined): boolean {
  return isPhoneAuthEmail(email) || isGuestPseudoEmail(email);
}

/**
 * Normalizes a phone number to E.164 format for database lookup
 * @param phone - Raw phone input (e.g., "2179798989", "(217) 979-8989")
 * @param countryCode - Country code (default '+1')
 * @returns Normalized E.164 format or null if invalid
 */
export function normalizePhoneForLookup(
  phone: string,
  countryCode: CountryCode = '+1'
): string | null {
  const result = validateAndNormalizePhone(phone, countryCode);
  return result.isValid ? result.normalized : null;
}
